<script setup>
import {ref, computed, onMounted} from 'vue'
import {useRouter} from 'vue-router'
import {useFormsStore} from '@/stores/forms'
import {useFormStore} from '@/stores/form'
import {useComponentsStore} from '@/stores/components'
import {useThemesStore} from '@/stores/themes'
import Form from '@/common/form/Form.vue'
import FormSaveBtn from '@/common/form/FormSaveBtn.vue'

const props = defineProps({
    modal: {type: Object, required: true},
    options: {type: Object, required: true},
})

const emit = defineEmits(['hide-modal'])

const router = useRouter()

const formsStore = useFormsStore()
const componentsStore = useComponentsStore()
const themesStore = useThemesStore()

// --- State ---

const id = ref(props.options.id || 0)
const modalTitle = ref('')

// --- Computed ---

const tableName = computed(() => themesStore.tableName)
const sectionName = computed(() => themesStore.sectionName)
const formId = computed(() => `form:${tableName.value}:${id.value}`)

// Dynamic Pinia Form Store Helper
const activeFormStore = computed(() => formId.value ? useFormStore(formId.value) : null)

/**
 * Check if the specific form store has been initialized with fields.
 * This prevents component render errors during async initialization.
 */
const formExists = computed(() => {
    return !!activeFormStore.value?.fields && activeFormStore.value.fields.length > 0
})

// --- Methods ---

const getType = () => {
    const types = {
        page_templates: 'page',
        page_sections: 'section',
        page_containers: 'container',
        content_types: 'contentType'
    }
    return types[sectionName.value] || ''
}

const setModalTitle = () => {
    const titles = {
        themes_css: 'CSS',
        themes_javascript: 'script',
        content_types: 'content type',
        page_templates: 'page template',
        page_sections: 'page section',
        page_containers: 'page container',
        templates_templates: 'template'
    }
    const prefix = id.value ? 'Edit ' : 'New '
    modalTitle.value = prefix + (titles[sectionName.value] || 'item')
}

const createForm = () => {
    const fieldNames = ['title']

    switch (sectionName.value) {
        case 'page_templates':
            fieldNames.push('cssGroupName')
            break
        case 'content_types':
            fieldNames.push(
                'contentType', 'componentId', 'filterAttribute',
                'lazyLoad', 'pageEditorGroup'
            )
            break
        case 'themes_css':
            fieldNames.push('mode', 'groups')
            break
        default:
            fieldNames.push('pageHeader', 'pageFooter')
    }

    // eslint-disable-next-line vue/no-mutating-props
    props.modal.formId = formId.value

    formsStore.createForm({
        tableName: tableName.value,
        id: id.value,
        fieldNames: fieldNames,
        currentData: {
            type: getType()
        },
        onInit: () => {
            const formStore = useFormStore(formId.value)
            if (formStore.fields?.length > 0) {
                formStore.fields[0].autofocus = true
            }
        },
        onSave: (obj) => {
            if (!id.value) {
                themesStore.setItems()
                router.push({
                    name: 'themesEditor',
                    query: {section: sectionName.value, id: obj.id}
                })

                if (sectionName.value === 'content_types') {
                    componentsStore.init()
                }
            }

            setTimeout(() => {
                emit('hide-modal')
            }, 100)
        }
    })
}

const deleteItem = () => {
    themesStore.deleteItem({
        router: router,
        id: id.value,
        onSuccess: () => {
            emit('hide-modal')
        }
    })
}

// --- Lifecycle ---

onMounted(() => {
    createForm()
    setModalTitle()
})
</script>

<template>
    <div class="modal-dialog modal-lg">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">{{ modalTitle }}</h4>
                <button
                    type="button"
                    class="btn-close"
                    aria-label="Close"
                    @click="$emit('hide-modal')"
                ></button>
            </div>

            <div class="modal-body">
                <Form v-if="formExists" :id="id" :form-id="formId"/>

                <div v-else class="p-5 text-center">
                    <div class="spinner-border text-secondary" role="status"></div>
                </div>
            </div>

            <div class="modal-footer d-flex justify-content-between">
                <div>
                    <button
                        v-if="id"
                        class="btn btn-outline-danger"
                        type="button"
                        @click.prevent="deleteItem"
                    >
                        <i class="bi-trash3 me-1"/> Delete
                    </button>
                </div>

                <FormSaveBtn v-if="formExists" :form-id="formId"/>
            </div>
        </div>
    </div>
</template>